import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Mail, ArrowRight, ArrowLeft } from 'lucide-react';
import { ApiApi } from 'api-client';
import { getApiConfiguration } from '../../api/utils';
import { Button } from '../../components/ui/button';

const ForgotPassword: React.FC = () => {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setMessage('');
        setError('');

        try {
            const client = new ApiApi(getApiConfiguration());
            await client.apiAuthPasswordResetCreate({
                customPasswordReset: { email }
            });

            setMessage('Password reset email has been sent. Please check your inbox.');
            setEmail('');
        } catch (err) {
            setError('Failed to send password reset email. Please try again.');
            console.error('Error:', err);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#0A0F1C] flex items-center justify-center p-6">
            <div className="w-full max-w-md">
                <div className="bg-[#0F1629] rounded-xl border border-gray-800/50 p-8">
                    {/* Header */}
                    <div className="flex items-center justify-center mb-8">
                        <Shield className="h-10 w-10 text-blue-500" />
                        <h1 className="text-2xl font-bold text-white ml-3">Flawtrack</h1>
                    </div>

                    {/* Title */}
                    <div className="text-center mb-8">
                        <h2 className="text-xl font-semibold text-white">Forgot Password?</h2>
                        <p className="text-sm text-gray-400 mt-1">We'll send you a password reset link</p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                                Email Address
                            </label>
                            <input
                                type="email"
                                id="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full px-3 py-2 bg-[#1A2235] border border-gray-800 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="Enter your email"
                                required
                            />
                        </div>

                        {message && (
                            <div className="p-4 bg-blue-500/10 text-blue-500 rounded-lg border border-blue-500/20">
                                {message}
                            </div>
                        )}

                        {error && (
                            <div className="p-4 bg-red-500/10 text-red-500 rounded-lg border border-red-500/20">
                                {error}
                            </div>
                        )}

                        <Button
                            type="submit"
                            disabled={isLoading}
                            className="w-full"
                        >
                            <Mail className="w-4 h-4 mr-2" />
                            {isLoading ? 'Sending...' : 'Send Reset Link'}
                            <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>

                        <Button
                            variant="outline"
                            onClick={() => navigate('/login')}
                            className="w-full"
                        >
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Back to Login
                        </Button>
                    </form>
                </div>
            </div>
        </div>
  );
};

export default ForgotPassword;